import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { Action, Subject } from '@erp/authz';
import { ProductModule } from '@erp/shared-types';
import { fetchEnhancedSalesOrderUiEnabled } from '@/api/org/config/useEnhancedSalesOrderUi';
import {
  Route as pathlessAuthenticatedRoute,
  requireModules,
  requirePermission,
} from '../../route';

// OMS v3 review page at the unversioned path. Its components intentionally
// stay in ../../sales-orders-v3/$draftId/ (folder not renamed to avoid churn).
const SalesOrderV3DetailPage = lazyRouteComponent(
  () => import('../../sales-orders-v3/$draftId/SalesOrderV3DetailPage'),
);

export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-orders/$draftId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrderDraftV3 },
        ],
      },
    },
  },
  component: SalesOrderV3DetailPage,
  // Shareable UI state for the review screen: active content tab, active source
  // rail tab, and whether the workflow pipeline strip is expanded.
  validateSearch: z.object({
    tab: z.enum(['details', 'lines', 'margin']).optional(),
    rail: z.enum(['activity', 'emails', 'document']).optional(),
    pipeline: z.coerce.boolean().optional(),
  }),
  loader: () => ({
    crumb: 'Sales Order',
    // The breadcrumb navigates back to the list, not the current detail page.
    crumbTo: '/sales-orders',
  }),
  beforeLoad: async ({ context, params }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
    // TEMPORARY redirect — remove after all tenants migrate to OMS v3.
    // A legacy org can only hold legacy order ids, so send the id to the v2
    // detail page. Runs before the permission check so a user without
    // SalesOrderDraftV3 is redirected, not bounced to /unauthorized.
    if (!(await fetchEnhancedSalesOrderUiEnabled(context.queryClient))) {
      throw redirect({
        to: '/sales-orders-v2/$salesOrderId',
        params: { salesOrderId: params.draftId },
      });
    }
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SalesOrderDraftV3,
    });
  },
});
